import React, { useState, useRef, useEffect } from 'react';
import { StudioSession } from '../../types';
import { buildSuiteCrossUrl, getGhostSignInUrl, clearGhostSession } from '../../lib/ghostSession';
import { handleGhostSignInClick } from '../../config/auth';
import {
  Layers,
  Activity,
  Calculator,
  Bookmark,
  ExternalLink,
  ChevronDown,
  LogIn,
  LogOut,
  Share2,
  Check,
  TrendingUp,
} from 'lucide-react';

interface HeaderProps {
  currentTab: 'comps' | 'arbitrage' | 'radar' | 'sandbox' | 'workspace';
  onSelectTab: (tab: 'comps' | 'arbitrage' | 'radar' | 'sandbox' | 'workspace') => void;
  session: StudioSession;
  onOpenAuthModal: () => void;
  onOpenShareModal?: () => void;
  savedScenariosCount: number;
}

export const Header: React.FC<HeaderProps> = ({
  currentTab,
  onSelectTab,
  session,
  onOpenAuthModal,
  onOpenShareModal,
  savedScenariosCount,
}) => {
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSignOut = () => {
    clearGhostSession();
    window.location.reload();
  };

  return (
    <header className="border-b border-[#1a253b] bg-[#0c121e] sticky top-0 z-40 select-none">
      <div className="max-w-[1920px] mx-auto px-4 h-14 flex items-center justify-between gap-4">
        {/* Left: Brand Identity & Suite Switcher */}
        <div className="flex items-center space-x-4">
          <a
            href="https://aixenergy.io"
            target="_blank"
            rel="noreferrer"
            className="group flex shrink-0 items-center gap-2 transition hover:opacity-95"
            title="AIxEnergy — Visit aixenergy.io"
          >
            <img
              src="/aixenergy_logo.jpg"
              alt="AIxEnergy"
              className="h-6 w-6 rounded-full object-cover ring-1 ring-[#1a253b] transition-transform group-hover:scale-105"
              onError={(e) => {
                (e.currentTarget as HTMLElement).style.display = 'none';
              }}
            />
            <span className="truncate text-xs sm:text-sm font-semibold tracking-tight">
              <span className="text-white group-hover:text-[#2979ff] transition-colors font-bold">AIx</span>
              <span className="text-[#2979ff] font-bold">Energy</span>
              <span className="text-slate-400 font-medium"> | Studio</span>
            </span>
          </a>

          {/* Unified Cross-Product Suite Switcher */}
          <div className="hidden lg:flex items-center space-x-2 pl-3 border-l border-[#1a253b] text-xs font-mono">
            <a
              href={buildSuiteCrossUrl('https://map.aixenergy.io')}
              target="_blank"
              rel="noreferrer"
              className="px-2 py-0.5 rounded text-slate-400 hover:text-white hover:bg-[#121a2d] transition"
              title="AIxEnergy Map — Physical & Spatial Site Diligence"
            >
              MAP
            </a>
            <span className="text-slate-600">/</span>
            <a
              href={buildSuiteCrossUrl('https://monitor.aixenergy.io')}
              target="_blank"
              rel="noreferrer"
              className="px-2 py-0.5 rounded text-slate-400 hover:text-white hover:bg-[#121a2d] transition"
              title="AIxEnergy Monitor — Grid Interconnection Queues"
            >
              MONITOR
            </a>
            <span className="text-slate-600">/</span>
            <a
              href={buildSuiteCrossUrl('https://capital.aixenergy.io')}
              target="_blank"
              rel="noreferrer"
              className="px-2 py-0.5 rounded text-slate-400 hover:text-white hover:bg-[#121a2d] transition"
              title="AIxEnergy Capital — Capital Formation & Financing Ledgers"
            >
              CAPITAL
            </a>
            <span className="text-slate-600">/</span>
            <span
              className="px-2 py-0.5 rounded text-white bg-[#2979ff]/15 font-semibold text-[#2979ff] border border-[#2979ff]/30"
              title="AIxEnergy Studio — Transaction Comps & Underwriting"
            >
              STUDIO
            </span>
          </div>
        </div>

        {/* Center: Analytical Mode Tabs */}
        <nav className="flex items-center space-x-1 bg-[#090e1a] p-1 rounded-lg border border-[#1a253b]">
          <button
            onClick={() => onSelectTab('comps')}
            className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              currentTab === 'comps'
                ? 'bg-[#162035] text-white shadow-sm font-semibold border border-[#2979ff]/40 text-[#2979ff]'
                : 'text-slate-400 hover:text-slate-200 hover:bg-[#121a2d]'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>TRANSACTIONS</span>
          </button>

          <button
            onClick={() => onSelectTab('arbitrage')}
            className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              currentTab === 'arbitrage'
                ? 'bg-[#162035] text-white shadow-sm font-semibold border border-[#2979ff]/40 text-[#2979ff]'
                : 'text-slate-400 hover:text-slate-200 hover:bg-[#121a2d]'
            }`}
            title="Queue-to-Deed Valuation Spread & Power Arbitrage"
          >
            <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
            <span>ARBITRAGE</span>
          </button>

          <button
            onClick={() => onSelectTab('radar')}
            className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              currentTab === 'radar'
                ? 'bg-[#162035] text-white shadow-sm font-semibold border border-[#2979ff]/40 text-[#2979ff]'
                : 'text-slate-400 hover:text-slate-200 hover:bg-[#121a2d]'
            }`}
          >
            <Activity className="w-3.5 h-3.5" />
            <span>QUEUE RADAR</span>
          </button>

          <button
            onClick={() => onSelectTab('sandbox')}
            className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              currentTab === 'sandbox'
                ? 'bg-[#162035] text-white shadow-sm font-semibold border border-[#2979ff]/40 text-[#2979ff]'
                : 'text-slate-400 hover:text-slate-200 hover:bg-[#121a2d]'
            }`}
          >
            <Calculator className="w-3.5 h-3.5" />
            <span>UNDERWRITING</span>
          </button>

          <button
            onClick={() => onSelectTab('workspace')}
            className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              currentTab === 'workspace'
                ? 'bg-[#162035] text-white shadow-sm font-semibold border border-[#2979ff]/40 text-[#2979ff]'
                : 'text-slate-400 hover:text-slate-200 hover:bg-[#121a2d]'
            }`}
          >
            <Bookmark className="w-3.5 h-3.5" />
            <span>WORKSPACE</span>
            {savedScenariosCount > 0 && (
              <span className="px-1.5 py-0.2 bg-[#2979ff]/20 text-[#2979ff] text-[10px] font-mono rounded-full font-bold">
                {savedScenariosCount}
              </span>
            )}
          </button>
        </nav>

        {/* Right: Actions & User Access Level */}
        <div className="flex items-center space-x-3">
          {/* Share Link Button */}
          <button
            onClick={onOpenShareModal || handleShare}
            className="hidden sm:flex items-center space-x-1.5 px-2.5 py-1 text-xs text-slate-300 hover:text-white hover:bg-[#121a2d] border border-[#1a253b] hover:border-[#2979ff]/40 rounded-md transition"
            title="Share deal intelligence or copy link"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5 text-[#2979ff]" />}
            <span>{copied ? 'Copied' : 'Share'}</span>
          </button>

          {/* Member Access Level Control */}
          <div className="relative" ref={dropdownRef}>
            {session.authenticated ? (
              <div>
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className="flex items-center space-x-2 px-2.5 py-1.5 bg-[#121a2d] hover:bg-[#1a253b] border border-[#1a253b] rounded-lg transition"
                >
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <div className="text-left font-mono">
                    <div className="text-xs font-semibold text-white leading-none">
                      {session.name || session.email?.split('@')[0] || 'Member'}
                    </div>
                    <div className="text-[10px] text-[#2979ff] uppercase leading-none mt-0.5 font-bold">
                      {session.tier.replace(/_/g, ' ')}
                    </div>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400 ml-1" />
                </button>

                {userDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-[#0c121e] border border-[#1a253b] rounded-xl shadow-2xl py-2 z-50 text-xs">
                    <div className="px-3 py-2 border-b border-[#1a253b]">
                      <div className="font-semibold text-white">{session.name || 'AIxEnergy Member'}</div>
                      <div className="text-slate-400 truncate text-[11px] font-mono">{session.email}</div>
                      <div className="mt-1">
                        <span className="px-2 py-0.5 rounded text-[10px] font-mono uppercase bg-[#2979ff]/15 text-[#2979ff] border border-[#2979ff]/30 font-bold">
                          Tier: {session.tier}
                        </span>
                      </div>
                    </div>

                    <div className="py-1">
                      <button
                        onClick={() => {
                          setUserDropdownOpen(false);
                          onOpenAuthModal();
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-[#121a2d] text-slate-300 hover:text-white transition flex items-center justify-between"
                      >
                        <span>Switch Access Tier (Dev)</span>
                        <span className="text-[10px] font-mono text-slate-500">Test</span>
                      </button>
                      <a
                        href="https://www.aixenergy.io/#/portal/account"
                        target="_blank"
                        rel="noreferrer"
                        className="w-full text-left px-3 py-2 hover:bg-[#121a2d] text-slate-300 hover:text-white transition flex items-center justify-between"
                      >
                        <span>Manage Ghost Membership</span>
                        <ExternalLink className="w-3 h-3 text-slate-500" />
                      </a>
                    </div>

                    <div className="pt-1 border-t border-[#1a253b]">
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-3 py-2 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 transition flex items-center space-x-2"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <a
                  href={getGhostSignInUrl()}
                  onClick={(e) => handleGhostSignInClick(e, getGhostSignInUrl())}
                  className="flex items-center space-x-1.5 px-3 py-1.5 bg-[#2979ff] hover:bg-[#1c68e8] text-white rounded-lg text-xs font-semibold shadow-md shadow-[#2979ff]/20 transition cursor-pointer"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  <span>Sign In</span>
                </a>
                <button
                  onClick={onOpenAuthModal}
                  className="px-2 py-1.5 text-[11px] font-mono text-slate-400 hover:text-slate-200 border border-[#1a253b] hover:border-slate-700 rounded-lg transition"
                  title="Dev Tier Switcher"
                >
                  Tiers
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
